import { Link } from 'react-router-dom'
import { PublicNavBar } from '../components/PublicNavBar'
import { PublicFooter } from '../components/PublicFooter'
import { usePageMeta } from '../hooks/usePageMeta'
import { pageMeta } from '../seo/pageMeta'

const sections = [
  {
    title: 'Describe Your Idea',
    icon: (
      <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    body: 'Type a plain-English description of what you want to build — "A dashboard for tracking expenses" or "CLI tool to convert CSV to JSON". Factory Nexus reads keywords in your prompt to pick the right language, framework, and project type. See the keyword reference below for tips.',
  },
  {
    title: 'Watch It Build in Real Time',
    icon: (
      <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    body: 'Once you submit your prompt, Factory Nexus creates the project and starts an AI agent to build it. You get a split-screen view: build progress on the left, a live preview of your running app on the right. Builds typically take 2–5 minutes — drop your email during the build and we’ll send you a link when it’s ready.',
  },
  {
    title: 'Save & Keep Building',
    icon: (
      <svg className="w-8 h-8 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
      </svg>
    ),
    body: 'Happy with what you see? Sign up to claim your project. You can keep chatting with the agent to add features, push to GitHub, edit files directly in the browser, and run tests — all from one place.',
  },
]

export default function GettingStarted() {
  usePageMeta(pageMeta('/getting-started'))

  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-100">
      <PublicNavBar />

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-bold mb-3 text-gray-900 dark:text-white">How Factory Nexus Works</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-12 text-lg">
          Go from idea to running app in minutes. Here's how.
        </p>

        <div className="space-y-10">
          {sections.map((section, i) => (
            <div key={i} className="flex gap-5">
              <div className="flex-shrink-0 mt-1">
                <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                  {section.icon}
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                  <span className="text-primary-500 mr-2">{i + 1}.</span>
                  {section.title}
                </h2>
                <p className="text-gray-500 dark:text-gray-400 leading-relaxed">{section.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Prompt keyword reference */}
        <div className="mt-16 p-6 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-2xl">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Prompt Keyword Reference</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            Factory Nexus detects these keywords in your prompt to choose the right project type and language.
          </p>

          <div className="space-y-5">
            {/* Project type keywords */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">Project Type</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-sky-100 dark:border-sky-800">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-sky-600 dark:text-sky-400 bg-sky-100 dark:bg-sky-900/40 px-2 py-0.5 rounded">UI</span>
                    <span className="text-xs text-gray-400">Web apps with a visual interface</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {['website', 'dashboard', 'react', 'vue', 'angular', 'frontend', 'landing page', 'ui'].map(kw => (
                      <code key={kw} className="text-xs px-1.5 py-0.5 bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 rounded font-mono">{kw}</code>
                    ))}
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-sky-100 dark:border-sky-800">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 rounded">Full-Stack</span>
                    <span className="text-xs text-gray-400">Frontend + backend together</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {['fullstack', 'full-stack', 'full stack'].map(kw => (
                      <code key={kw} className="text-xs px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded font-mono">{kw}</code>
                    ))}
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-sky-100 dark:border-sky-800">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 rounded">Terminal / CLI</span>
                    <span className="text-xs text-gray-400">Command-line tools and scripts</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Default when no UI keywords are detected. Use these to be explicit:</p>
                  <div className="flex flex-wrap gap-1">
                    {['CLI', 'command line', 'script', 'terminal', 'API', 'server'].map(kw => (
                      <code key={kw} className="text-xs px-1.5 py-0.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded font-mono">{kw}</code>
                    ))}
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-sky-100 dark:border-sky-800">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-violet-600 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/40 px-2 py-0.5 rounded">Library</span>
                    <span className="text-xs text-gray-400">Reusable packages and SDKs</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {['library', 'sdk', 'package', 'module'].map(kw => (
                      <code key={kw} className="text-xs px-1.5 py-0.5 bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded font-mono">{kw}</code>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Language keywords */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">Language</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Defaults to TypeScript for web apps. Mention a language or framework to override:
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'TypeScript', keywords: 'react, vue, angular, next.js, svelte, website, web app' },
                  { label: 'Go', keywords: 'golang, go api, go cli' },
                  { label: 'Python', keywords: 'python, django, flask, fastapi' },
                  { label: 'Rust', keywords: 'rust, cargo' },
                  { label: 'Java', keywords: 'java, spring, maven, gradle' },
                ].map(lang => (
                  <div key={lang.label} className="flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{lang.label}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">{lang.keywords}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Example prompts */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">Example Prompts</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { prompt: '"A React dashboard for tracking expenses"', result: 'TypeScript / UI' },
                  { prompt: '"CLI tool to convert CSV to JSON"', result: 'TypeScript / Terminal' },
                  { prompt: '"Python Flask API for a recipe app"', result: 'Python / Terminal' },
                  { prompt: '"Full-stack blog with authentication"', result: 'TypeScript / Full-Stack' },
                  { prompt: '"Go library for parsing YAML configs"', result: 'Go / Library' },
                  { prompt: '"Rust CLI to batch resize images"', result: 'Rust / Terminal' },
                ].map(ex => (
                  <div key={ex.prompt} className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
                    <span className="text-xs text-gray-600 dark:text-gray-300 font-mono">{ex.prompt}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-2 whitespace-nowrap">{ex.result}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <Link
            to="/"
            className="inline-block px-8 py-3 bg-gradient-to-r from-primary-600 to-orange-500 text-white font-semibold rounded-lg hover:from-primary-500 hover:to-orange-400 transition-all shadow-lg shadow-orange-500/20"
          >
            Try It Now
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">No account needed to try your first build.</p>
        </div>
      </div>

      <PublicFooter />
    </div>
  )
}
